import styled from '@emotion/styled';
import React, { FC } from 'react';
import { Button } from 'src/shared/components/Button';
import { color, font } from 'src/shared/styles/variables';

const Wrapper = styled.div<{ accentColor?: string }>(({ accentColor }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: accentColor,
    borderRadius: '8px',
    marginBottom: '40px',
}));

const DescriptionWrapper = styled.div({
    padding: '72px 72px 72px 20px',
    maxWidth: '824px',
});

const Description = styled.p<{ textColor?: string }>(
    ({ textColor }) => ({
        color: textColor,
        lineHeight: '28px',
        marginBottom: 22,
    }),
    font.roman(font.size.large),
);

const Subtitle = styled.h3<{ textColor?: string }>(
    ({ textColor }) => ({
        color: textColor,
    }),
    font.roman(font.size.mediumExtended),
);

const Title = styled.h2<{ textColor?: string }>(
    ({ textColor }) => ({
        color: textColor ?? color.white,
        marginTop: 20,
        marginBottom: 10,
    }),
    font.black(font.size.extraLarge),
);

const BannerImageWrapper = styled.div({
    position: 'relative',
});

const BannerImage = styled.img({
    position: 'absolute',
    right: 0,
    bottom: 0,
});

interface Props {
    bannerSubtitle?: string;
    bannerTitle?: string;
    bannerContent?: string;
    bannerCtaText?: string;
    bannerImage?: string;
    titleColor?: string;
    accentColor?: string;
    onClick?: () => void;
    href?: string;
    to?: string;
}

export enum TopicOfTheMonthBannerTestID {
    Wrapper = 'TopicOfTheMonthBanner.Wrapper',
    Title = 'TopicOfTheMonthBanner.Title',
    Subtitle = 'TopicOfTheMonthBanner.Subtitle',
    BannerContent = 'TopicOfTheMonthBanner.BannerContent',
    BannerImage = 'TopicOfTheMonthBanner.BannerImage',
    CtaButton = 'TopicOfTheMonthBanner.CtaButton',
}

export const TopicOfTheMonthBanner: FC<Props> = ({
    accentColor,
    titleColor,
    bannerSubtitle,
    bannerTitle,
    bannerContent: description,
    bannerCtaText,
    bannerImage,
    onClick,
    href,
    to,
}) => {
    const buttonProps = {
        testID: TopicOfTheMonthBannerTestID.CtaButton,
        rounded: true,
        horizontalPadding: 'small',
        type: 'white2',
    } as const;

    return (
        <Wrapper accentColor={accentColor} data-testid={TopicOfTheMonthBannerTestID.Wrapper}>
            <DescriptionWrapper>
                {bannerSubtitle && (
                    <Subtitle
                        data-testid={TopicOfTheMonthBannerTestID.Subtitle}
                        textColor={titleColor}
                    >
                        {bannerSubtitle}
                    </Subtitle>
                )}
                {bannerTitle && (
                    <Title data-testid={TopicOfTheMonthBannerTestID.Title} textColor={titleColor}>
                        {bannerTitle}
                    </Title>
                )}
                {description && (
                    <Description
                        data-testid={TopicOfTheMonthBannerTestID.BannerContent}
                        textColor={titleColor}
                    >
                        {description}
                    </Description>
                )}
                {bannerCtaText ? (
                    to ? (
                        <Button {...buttonProps} to={to}>
                            {bannerCtaText}
                        </Button>
                    ) : href ? (
                        <Button {...buttonProps} href={href} target="_blank">
                            {bannerCtaText}
                        </Button>
                    ) : onClick ? (
                        <Button {...buttonProps} onClick={onClick}>
                            {bannerCtaText}
                        </Button>
                    ) : (
                        <Button {...buttonProps} onClick={() => undefined}>
                            {bannerCtaText}
                        </Button>
                    )
                ) : null}
            </DescriptionWrapper>
            <BannerImageWrapper>
                {bannerImage && (
                    <BannerImage
                        data-testid={TopicOfTheMonthBannerTestID.BannerImage}
                        src={bannerImage}
                        alt="topic of the month banner image"
                    />
                )}
            </BannerImageWrapper>
        </Wrapper>
    );
};
